/**
 * DocumentVatRegisterEditor.tsx — Dokumenty: EDYTOWALNA siatka RĘCZNEGO rejestru VAT faktury.
 *
 * Architektura mirror `DocumentItemsEditor`/`DecreeLinesEditor` (patrz nagłówki tamtych
 * plików) — w PEŁNI kontrolowany z zewnątrz (`value`/`onChange`), ZERO własnego
 * fetch/mutate stanu rejestru. Zapis (payload POST/PATCH faktury, pole `vatRegister`)
 * i wpięcie edytora robią strony faktur (strony szczegółów: stan `vatRegister` + `columnSettings`
 * z układu); sam komponent niczego nie zapisuje.
 *
 * Zestaw kolumn jest STAŁY (kolejność WF z `VAT_REGISTER_LAYOUT_COLUMN_IDS`, wybór i układ przez
 * `visibleVatRegisterColumns`) — rejestr VAT nie ma pól dodatkowych. Gdy strona poda `columnSettings` (`element.settings` klocka `vat_register_view`),
 * układ z kreatora formatki wybiera i układa kolumny (kolejność, widoczność, własna etykieta,
 * wyrównanie, szerokość STARTOWA; wyrównanie dotyczy też wartości list i pola wyboru GTU); bez
 * niego tabela wygląda jak dotąd. Kolumna „Akcje" nie jest
 * kolumną układu — w edycji zawsze stoi na końcu, w dotychczasowej szerokości. Liczba miejsc po
 * przecinku z układu nie dotyczy edytora: kwoty są tu polami wejściowymi i pokazują wartość surową
 * (formatuje je tylko widok odczytu `DocumentVatRegisterSection`).
 *
 * Wiersz filtrujący (drugi wiersz nagłówka, klientowy/niedestrukcyjny) i uchwyt resize
 * kolumn (sesyjny, `<colgroup>`/`<col>`, zero persystencji; przeciągnięcie działa na wierzchu
 * szerokości startowej z układu) są SKOPIOWANE z
 * `DocumentItemsEditor.tsx` (decyzja: `DocumentItemsEditor` ma zostać NIETKNIĘTY;
 * ekstrakcja do wspólnego prymitywu tabeli odłożona).
 *
 * `editable=false` — WBUDOWANY tryb read-only (nie delegacja do osobnego komponentu
 * `DocumentVatRegisterSection`, który operuje na innym kształcie danych — wiersz
 * SERWEROWY z `id`/`lineNumber`, nie draft): wszystkie kontrolki dostają `disabled`,
 * kolumna akcji (usuwanie wiersza) i przycisk „Dodaj wiersz" znikają.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  effectiveColumnAlign,
  type EmbeddedTableColumnAlign,
  VAT_TAX_CATEGORIES,
} from '@saas/shared';
import { Plus, Search, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { MultiSelect, type MultiSelectOption } from '@/components/MultiSelect';
import { useDictionary } from '@/hooks/useDictionary';
import { normalizeDecimalInput } from '@/lib/documents/document-items-draft';
import {
  computeVatAndGrossFromNet,
  type DocumentVatRegisterColumnId as ColumnId,
  type DocumentVatRegisterRowDraft,
  emptyVatRegisterRow,
  resolveVatRateValue,
  VAT_REGISTER_COLUMN_HEADER_KEYS,
  type VatRegisterCategoryAbcdDraft,
  vatTaxCategoryLabel,
} from '@/lib/documents/document-vat-register';
import {
  columnAlignClass,
  listControlValueAlignClass,
  multiSelectValueAlignClass,
} from '@/lib/embedded-table/cells';
import {
  type VatRegisterLayoutColumnId as LayoutColumnId,
  visibleVatRegisterColumns,
} from '@/lib/embedded-table/vat-register-descriptors';

export interface DocumentVatRegisterEditorProps {
  value: DocumentVatRegisterRowDraft[];
  onChange: (next: DocumentVatRegisterRowDraft[]) => void;
  /** Kod słownika rejestrów — strona faktury poda właściwy (zakup vs sprzedaż); edytor
   * sam nie wie, na jakiej klasie dokumentu jest osadzony. */
  registerDictionaryCode: 'vat_registers_purchase' | 'vat_registers_sale';
  /** Żywe netto nagłówka faktury (form state strony, NIE zrzut serwera) — do wyliczenia
   * „reszty netto" nowego wiersza. `null`/`undefined` → nowy wiersz startuje z pustym netto. */
  amountNet: string | null | undefined;
  /** `false` → tryb odczytu wbudowany w ten sam komponent (patrz nagłówek pliku). */
  editable: boolean;
  /** Ustawienia elementu `vat_register_view` z layoutu (`element.settings`): układ kolumn
   * (`tableColumns` + maska `hiddenColumnIds`) — ten sam zapis co w widoku odczytu. Brak ustawień
   * → stałe kolumny w kolejności WF, dotychczasowe szerokości i wygląd. Kolumny wymagane i
   * zablokowane (Lp., rejestr, stawka, netto, VAT, brutto) są pokazane zawsze. */
  columnSettings?: Record<string, unknown>;
  'data-testid'?: string;
}

const th = 'px-2 py-2 text-xs font-semibold text-text-secondary whitespace-nowrap';
const td = 'px-2 py-1 align-top';

/** Domyślne szerokości (px) — startowe, gdy układ kolumny nie ma szerokości; przeciągnięcie
 * nagłówka zmienia je tylko w stanie sesji komponentu, zerowanym po remouncie
 * (mirror `DocumentItemsEditor.DEFAULT_COLUMN_WIDTHS`). */
const DEFAULT_COLUMN_WIDTHS: Record<ColumnId, number> = {
  registerCode: 176,
  categoryAbcd: 96,
  taxCategory: 176,
  vatRateCode: 112,
  valueNet: 112,
  valueVat: 112,
  valueGross: 112,
  valueNetCurrency: 112,
  valueVatCurrency: 112,
  valueGrossCurrency: 112,
  service: 72,
  period: 128,
  lineNumber: 56,
  ue: 64,
  gtuCodes: 176,
  actions: 56,
};

/** Minimalna szerokość kolumny podczas resize (px) — mirror `DocumentItemsEditor`. */
const MIN_COLUMN_WIDTH = 48;

/**
 * Pola drafta filtrowane per kolumna (substring, case-insensitive) — mirror
 * `DocumentItemsEditor.FILTER_FIELDS`. Niefiltrowalne: `lineNumber` (auto-numer),
 * `ue`/`service`/`gtuCodes` (checkbox/multiselect — filtr tekstowy nie pasuje do ich UI)
 * i `actions`. Kolumny „Opis" nie ma (zdjęta 7.09 — patrz `document-vat-register.ts`).
 */
const FILTER_FIELDS: Partial<Record<ColumnId, (row: DocumentVatRegisterRowDraft) => string>> = {
  registerCode: (row) => row.registerCode,
  categoryAbcd: (row) => row.categoryAbcd,
  taxCategory: (row) => row.taxCategory,
  vatRateCode: (row) => row.vatRateCode,
  valueNet: (row) => row.valueNet,
  valueVat: (row) => row.valueVat,
  valueGross: (row) => row.valueGross,
  valueNetCurrency: (row) => row.valueNetCurrency,
  valueVatCurrency: (row) => row.valueVatCurrency,
  valueGrossCurrency: (row) => row.valueGrossCurrency,
  period: (row) => row.period,
};

function isFilterableColumn(colId: ColumnId): boolean {
  return Boolean(FILTER_FIELDS[colId]);
}

/**
 * Uchwyt resize przy prawej krawędzi `<th>` — pominięty na ostatniej kolumnie (akcje).
 * Zdefiniowany na poziomie MODUŁU (nie wewnątrz `DocumentVatRegisterEditor`) — mirror
 * `DocumentItemsEditor.ColumnResizeHandle` (uzasadnienie w komentarzu tamtego pliku:
 * funkcja-komponent odtworzona przy KAŻDYM renderze rodzica gubiłaby fokus filtra).
 */
function ColumnResizeHandle({
  colId,
  isActive,
  label,
  onPointerDownStart,
}: {
  colId: ColumnId;
  isActive: boolean;
  label: string;
  onPointerDownStart: (e: React.PointerEvent<HTMLDivElement>, colId: ColumnId) => void;
}) {
  if (colId === 'actions') return null;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={(e) => onPointerDownStart(e, colId)}
      className={`absolute inset-y-0 right-0 w-1.5 cursor-col-resize touch-none select-none ${
        isActive ? 'bg-accent-500' : 'hover:bg-accent-400/50'
      }`}
      data-testid={`document-vat-register-col-resize-${colId}`}
    />
  );
}

/** Input filtra klientowego pod tytułem kolumny — pusta `<th>` gdy kolumna nie jest filtrowalna. */
function ColumnFilterCell({
  colId,
  value,
  ariaLabel,
  onChangeValue,
}: {
  colId: ColumnId;
  value: string;
  ariaLabel: string;
  onChangeValue: (colId: ColumnId, value: string) => void;
}) {
  if (!isFilterableColumn(colId)) return <th className="px-2 py-1" scope="col" />;
  return (
    <th className="px-2 py-1" scope="col">
      <Input
        prefixIcon={<Search className="h-3 w-3" />}
        value={value}
        onChange={(e) => onChangeValue(colId, e.target.value)}
        aria-label={ariaLabel}
        className="py-0.5 text-xs"
        data-testid={`document-vat-register-filter-${colId}`}
      />
    </th>
  );
}

export function DocumentVatRegisterEditor({
  value,
  onChange,
  registerDictionaryCode,
  amountNet,
  editable,
  columnSettings,
  'data-testid': testId = 'document-vat-register-editor',
}: DocumentVatRegisterEditorProps) {
  const { t } = useTranslation();
  /**
   * Rejestry VAT: pierwszeństwo ma słownik ZSYNCHRONIZOWANY z Symfonii (`fk_vat_registers`,
   * zaciągany przez synchronizację słowników integracji). Powód jest praktyczny: wybrany
   * kod musi dać się dopasować do rejestru po stronie FK przy wysyłce dokumentu — kody z
   * własnego, ręcznie prowadzonego słownika `vat_registers_*` do niczego się tam nie odnoszą.
   * Bez synchronizacji (tenant bez Symfonii albo przed pierwszym importem) zostaje słownik
   * lokalny, więc formatka działa jak wcześniej.
   */
  const fkRegisterDict = useDictionary('fk_vat_registers');
  const localRegisterDict = useDictionary(registerDictionaryCode);
  const vatDict = useDictionary('vat_rates');
  const gtuDict = useDictionary('gtu_codes');
  // Lista do <select> — TYLKO pozycje aktywne/selectable. Dopasowanie WARTOŚCI liczbowej
  // (przy zmianie stawki) czyta z PEŁNEJ listy (`vatAllItems`) — mirror `DocumentItemsEditor`
  // (kod wiersza wczytanego z serwera może wskazywać kod, który przestał być aktywny).
  const fkRegisterItems = fkRegisterDict.activeItems ?? [];
  const registerActiveItems =
    fkRegisterItems.length > 0 ? fkRegisterItems : (localRegisterDict.activeItems ?? []);
  const vatActiveItems = vatDict.activeItems ?? [];
  const vatAllItems = vatDict.items ?? [];
  const gtuOptions: MultiSelectOption[] = (gtuDict.activeItems ?? []).map((item) => ({
    value: item.code,
    label: item.label,
  }));

  // Kolumny układu (widoczne, w kolejności układu; bez układu: wszystkie w kolejności WF) + kolumna
  // akcji zawsze na końcu, tylko w edycji — nie jest kolumną układu.
  const layoutColumns = useMemo(
    () => visibleVatRegisterColumns(t, columnSettings),
    [t, columnSettings],
  );
  const layoutColById = new Map(layoutColumns.map(({ colId, col }) => [colId, col]));
  const columns: ColumnId[] = [
    ...layoutColumns.map(({ colId }) => colId),
    ...(editable ? (['actions'] as const) : []),
  ];

  /** Nagłówek kolumny: własna etykieta z układu albo etykieta deskryptora (jedno źródło dla
   * nagłówka, aria-label kontrolek komórek, filtra i uchwytu zmiany szerokości). */
  function labelOf(colId: ColumnId): string {
    if (colId === 'actions') return t(VAT_REGISTER_COLUMN_HEADER_KEYS.actions);
    const col = layoutColById.get(colId);
    // Każda renderowana kolumna pochodzi z `layoutColumns`, więc brak jej w mapie to błąd
    // programisty — jawnie, zamiast po cichu podstawionej etykiety domyślnej.
    if (!col) throw new Error(`DocumentVatRegisterEditor: kolumna „${colId}” spoza układu`);
    return col.label ?? col.descriptor.label;
  }

  /** Wyrównanie kolumny: jawne z układu, inaczej domyślne deskryptora / wg rodzaju. */
  function alignOf(colId: LayoutColumnId): EmbeddedTableColumnAlign {
    const col = layoutColById.get(colId);
    // Jak w `labelOf`: kolumna spoza układu to błąd programisty, nie powód do cichego „left”.
    if (!col) throw new Error(`DocumentVatRegisterEditor: kolumna „${colId}” spoza układu`);
    return effectiveColumnAlign(col);
  }

  /** Klasa wyrównania tekstu kolumny (nagłówek, pola `Input`, numer wiersza). */
  function alignClassOf(colId: LayoutColumnId): string {
    return columnAlignClass(alignOf(colId));
  }

  /** Wyrównanie WARTOŚCI kontrolki listy (`Dropdown`): sama klasa na komórce nie dosięga tekstu w
   * jej przycisku, więc idzie przez `valueClassName`. Kolumny list mają rodzaj tekstowy (domyślnie
   * do lewej) — bez jawnego wyrównania z układu kontrolka wygląda jak dotąd. */
  function dropdownValueClassOf(colId: LayoutColumnId): string | undefined {
    return listControlValueAlignClass(alignOf(colId));
  }

  /** Komórka z samym znacznikiem (checkbox): dotąd wyśrodkowana — zostaje tak, dopóki układ nie
   * ustawi wyrównania jawnie (domyślne wyrównanie kolumny logicznej to lewa, jak w odczycie). */
  function checkboxAlignClass(colId: LayoutColumnId): string {
    const align = layoutColById.get(colId)?.align;
    return align === undefined ? 'text-center' : columnAlignClass(align);
  }

  // ── sesyjny resize kolumn (mirror DocumentItemsEditor) ──────────────────────
  // Szerokość startowa: zapisana w układzie, inaczej domyślna kolumny; przeciągnięcie zapisuje
  // tylko nadpisanie sesyjne (niezapisywane), więc działa na wierzchu szerokości z układu.
  const [sessionWidths, setSessionWidths] = useState<Partial<Record<ColumnId, number>>>({});
  const [resizingColumn, setResizingColumn] = useState<ColumnId | null>(null);
  const resizeStartRef = useRef<{ startX: number; startWidth: number }>({
    startX: 0,
    startWidth: 0,
  });

  function columnWidthOf(colId: ColumnId): number {
    const session = sessionWidths[colId];
    if (session !== undefined) return session;
    if (colId === 'actions') return DEFAULT_COLUMN_WIDTHS.actions;
    return layoutColById.get(colId)?.width ?? DEFAULT_COLUMN_WIDTHS[colId];
  }

  function handleResizeStart(e: React.PointerEvent<HTMLDivElement>, colId: ColumnId) {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = {
      startX: e.clientX,
      startWidth: columnWidthOf(colId),
    };
    setResizingColumn(colId);
  }

  useEffect(() => {
    if (!resizingColumn) return;
    const activeColumn = resizingColumn;

    function handlePointerMove(e: PointerEvent) {
      const { startX, startWidth } = resizeStartRef.current;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (e.clientX - startX));
      setSessionWidths((prev) => ({ ...prev, [activeColumn]: nextWidth }));
    }
    function handlePointerUp() {
      setResizingColumn(null);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizingColumn]);

  // ── klientowy filtr per kolumna (niedestrukcyjny — mirror DocumentItemsEditor) ──
  const [filters, setFilters] = useState<Partial<Record<ColumnId, string>>>({});

  function setColumnFilter(colId: ColumnId, filterValue: string) {
    setFilters((prev) => ({ ...prev, [colId]: filterValue }));
  }

  // Filtr na kolumnie, która przestała być widoczna (zmiana układu), nie może dalej filtrować —
  // użytkownik nie widziałby ani pola filtra, ani powodu, dla którego wierszy ubyło.
  const activeFilters = (Object.entries(filters) as [ColumnId, string][]).filter(
    ([colId, filterValue]) => filterValue.trim() !== '' && columns.includes(colId),
  );
  const visibleRows = value
    .map((row, originalIndex) => ({ row, originalIndex }))
    .filter(({ row }) =>
      activeFilters.every(([colId, filterValue]) => {
        const query = filterValue.trim().toLowerCase();
        const getValue = FILTER_FIELDS[colId];
        return getValue ? getValue(row).toLowerCase().includes(query) : true;
      }),
    );

  function updateRow(key: string, patch: Partial<DocumentVatRegisterRowDraft>) {
    onChange(value.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addRow() {
    onChange([...value, emptyVatRegisterRow(amountNet, value)]);
  }

  function removeRow(key: string) {
    onChange(value.filter((row) => row.key !== key));
  }

  /** Zmiana netto → przelicza VAT/brutto ze STAWKĄ bieżącą wiersza (klientowy podgląd,
   * pola zostają dalej edytowalne — user może nadpisać wynik). */
  function onNetChange(key: string, raw: string) {
    const row = value.find((r) => r.key === key);
    const normalized = normalizeDecimalInput(raw);
    const { valueVat, valueGross } = computeVatAndGrossFromNet(normalized, row?.vatRate ?? '');
    updateRow(key, { valueNet: normalized, valueVat, valueGross });
  }

  /** Zmiana stawki VAT → przelicza VAT/brutto z NETTEM bieżącym wiersza. */
  function onVatRateChange(key: string, code: string) {
    const row = value.find((r) => r.key === key);
    const vatRate = resolveVatRateValue(code, vatAllItems);
    const { valueVat, valueGross } = computeVatAndGrossFromNet(row?.valueNet ?? '', vatRate);
    updateRow(key, { vatRateCode: code, vatRate, valueVat, valueGross });
  }

  function resizeAriaLabel(colId: ColumnId): string {
    return t('tenant.documents.vatRegister.editor.resizeColumnAria', {
      column: labelOf(colId),
    });
  }

  function renderHeaderLabel(colId: ColumnId): React.ReactNode {
    if (colId === 'actions') {
      return <span className="sr-only">{labelOf('actions')}</span>;
    }
    return labelOf(colId);
  }

  function renderBodyCell(
    colId: ColumnId,
    row: DocumentVatRegisterRowDraft,
    index: number,
  ): React.ReactNode {
    switch (colId) {
      case 'registerCode':
        return (
          <td key={colId} className={td}>
            <Dropdown
              options={[
                { value: '', label: t('tenant.documents.vatRegister.editor.registerPlaceholder') },
                ...registerActiveItems.map((item) => ({ value: item.code, label: item.label })),
                // Kod z serwera nieobecny wśród aktywnych pozycji słownika (nieaktywny/ukryty
                // w międzyczasie) — syntetyczna opcja pokazuje rzeczywisty kod (mirror
                // `DocumentItemsEditor` FIX 3 dla vatRateCode).
                ...(row.registerCode &&
                !registerActiveItems.some((item) => item.code === row.registerCode)
                  ? [{ value: row.registerCode, label: row.registerCode }]
                  : []),
              ]}
              value={row.registerCode}
              onChange={(v) => updateRow(row.key, { registerCode: v })}
              disabled={!editable}
              valueClassName={dropdownValueClassOf('registerCode')}
              aria-label={labelOf('registerCode')}
              data-testid={`document-vat-register-code-${index}`}
            />
          </td>
        );
      case 'categoryAbcd':
        return (
          <td key={colId} className={td}>
            <Dropdown
              options={[
                { value: '', label: t('tenant.documents.vatRegister.category.none') },
                { value: 'A', label: 'A' },
                { value: 'B', label: 'B' },
                { value: 'C', label: 'C' },
                { value: 'D', label: 'D' },
              ]}
              value={row.categoryAbcd}
              onChange={(v) =>
                updateRow(row.key, { categoryAbcd: v as VatRegisterCategoryAbcdDraft })
              }
              disabled={!editable}
              valueClassName={dropdownValueClassOf('categoryAbcd')}
              aria-label={labelOf('categoryAbcd')}
              data-testid={`document-vat-register-category-${index}`}
            />
          </td>
        );
      case 'taxCategory':
        return (
          <td key={colId} className={td}>
            <Dropdown
              options={[
                { value: '', label: t('tenant.documents.vatRegister.taxCategory.none') },
                ...VAT_TAX_CATEGORIES.map((cat) => ({
                  value: cat.code,
                  label: vatTaxCategoryLabel(cat.code, t),
                })),
                // Kod z serwera spoza znanego zbioru (ręczna korekta w bazie) — syntetyczna
                // opcja pokazuje rzeczywistą wartość, mirror zachowania kolumny rejestru.
                ...(row.taxCategory &&
                !VAT_TAX_CATEGORIES.some((cat) => cat.code === row.taxCategory)
                  ? [{ value: row.taxCategory, label: row.taxCategory }]
                  : []),
              ]}
              value={row.taxCategory}
              onChange={(v) => updateRow(row.key, { taxCategory: v })}
              disabled={!editable}
              valueClassName={dropdownValueClassOf('taxCategory')}
              aria-label={labelOf('taxCategory')}
              data-testid={`document-vat-register-tax-category-${index}`}
            />
          </td>
        );
      case 'vatRateCode':
        return (
          <td key={colId} className={td}>
            <Dropdown
              options={[
                { value: '', label: t('tenant.documents.vatRegister.editor.vatPlaceholder') },
                ...vatActiveItems.map((item) => ({ value: item.code, label: item.label })),
                ...(row.vatRateCode && !vatActiveItems.some((item) => item.code === row.vatRateCode)
                  ? [{ value: row.vatRateCode, label: row.vatRateCode }]
                  : []),
              ]}
              value={row.vatRateCode}
              onChange={(v) => onVatRateChange(row.key, v)}
              disabled={!editable}
              valueClassName={dropdownValueClassOf('vatRateCode')}
              aria-label={labelOf('vatRateCode')}
              data-testid={`document-vat-register-rate-${index}`}
            />
          </td>
        );
      case 'valueNet':
        return (
          <td key={colId} className={td}>
            <Input
              inputMode="decimal"
              value={row.valueNet}
              onChange={(e) => onNetChange(row.key, e.target.value)}
              disabled={!editable}
              className={alignClassOf('valueNet')}
              aria-label={labelOf('valueNet')}
              data-testid={`document-vat-register-net-${index}`}
            />
          </td>
        );
      case 'valueVat':
        return (
          <td key={colId} className={td}>
            <Input
              inputMode="decimal"
              value={row.valueVat}
              onChange={(e) =>
                updateRow(row.key, { valueVat: normalizeDecimalInput(e.target.value) })
              }
              disabled={!editable}
              className={alignClassOf('valueVat')}
              aria-label={labelOf('valueVat')}
              data-testid={`document-vat-register-vat-${index}`}
            />
          </td>
        );
      case 'valueGross':
        return (
          <td key={colId} className={td}>
            <Input
              inputMode="decimal"
              value={row.valueGross}
              onChange={(e) =>
                updateRow(row.key, { valueGross: normalizeDecimalInput(e.target.value) })
              }
              disabled={!editable}
              className={alignClassOf('valueGross')}
              aria-label={labelOf('valueGross')}
              data-testid={`document-vat-register-gross-${index}`}
            />
          </td>
        );
      case 'valueNetCurrency':
      case 'valueVatCurrency':
      case 'valueGrossCurrency': {
        // Trzy kwoty walutowe zachowują się identycznie (zwykłe pola kwotowe, BEZ
        // przeliczania netto→VAT→brutto: kurs i zaokrąglenia w walucie są decyzją księgowej,
        // a rejestr jest zapisem ręcznym) — dlatego jedna gałąź, nie trzy kopie.
        const testIdSuffix =
          colId === 'valueNetCurrency' ? 'net' : colId === 'valueVatCurrency' ? 'vat' : 'gross';
        return (
          <td key={colId} className={td}>
            <Input
              inputMode="decimal"
              value={row[colId]}
              onChange={(e) =>
                updateRow(row.key, { [colId]: normalizeDecimalInput(e.target.value) })
              }
              disabled={!editable}
              className={alignClassOf(colId)}
              aria-label={labelOf(colId)}
              data-testid={`document-vat-register-${testIdSuffix}-currency-${index}`}
            />
          </td>
        );
      }
      case 'service':
        return (
          <td key={colId} className={`${td} ${checkboxAlignClass('service')}`}>
            <Checkbox
              checked={row.service}
              onChange={(e) => updateRow(row.key, { service: e.target.checked })}
              disabled={!editable}
              aria-label={labelOf('service')}
              data-testid={`document-vat-register-service-${index}`}
            />
          </td>
        );
      case 'period':
        return (
          <td key={colId} className={td}>
            <Input
              type="date"
              value={row.period}
              onChange={(e) => updateRow(row.key, { period: e.target.value })}
              disabled={!editable}
              className={alignClassOf('period')}
              aria-label={labelOf('period')}
              data-testid={`document-vat-register-period-${index}`}
            />
          </td>
        );
      case 'lineNumber':
        return (
          <td
            key={colId}
            className={`px-2 py-1 align-top text-text-tertiary tabular-nums ${alignClassOf('lineNumber')}`}
          >
            {index + 1}
          </td>
        );
      case 'ue':
        return (
          <td key={colId} className={`px-2 py-1 align-top ${checkboxAlignClass('ue')}`}>
            <Checkbox
              checked={row.ue}
              onChange={(e) => updateRow(row.key, { ue: e.target.checked })}
              disabled={!editable}
              aria-label={labelOf('ue')}
              data-testid={`document-vat-register-ue-${index}`}
            />
          </td>
        );
      case 'gtuCodes':
        // `MultiSelect` nie ma propa `aria-label` (tylko widoczny `label`, niepożądany
        // w gęstej siatce — zdublowałby nagłówek kolumny w każdym wierszu) — kolumna
        // ma dziś accessible name WYŁĄCZNIE z nagłówka `<th>`, nie z komórki. Znana
        // luka współdzielonego prymitywu, poza zakresem tego increment.
        return (
          <td key={colId} className={td}>
            <MultiSelect
              value={row.gtuCodes}
              onChange={(codes) => updateRow(row.key, { gtuCodes: codes })}
              options={gtuOptions}
              disabled={!editable}
              valueClassName={multiSelectValueAlignClass(alignOf('gtuCodes'))}
              data-testid={`document-vat-register-gtu-${index}`}
            />
          </td>
        );
      case 'actions':
        return (
          <td key={colId} className="px-2 py-1 align-top text-center">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => removeRow(row.key)}
              aria-label={t('tenant.documents.vatRegister.editor.delete', { row: index + 1 })}
              data-testid={`document-vat-register-remove-${index}`}
            >
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </td>
        );
      default:
        return null;
    }
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="overflow-x-auto rounded border border-border-primary">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            {columns.map((colId) => (
              <col
                key={colId}
                data-testid={`document-vat-register-col-${colId}`}
                style={{ width: `${columnWidthOf(colId)}px` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-border-primary bg-bg-secondary">
              {columns.map((colId) => (
                <th
                  key={colId}
                  className={`${th} ${colId === 'actions' ? 'text-left' : alignClassOf(colId)} relative`}
                  scope="col"
                >
                  {renderHeaderLabel(colId)}
                  <ColumnResizeHandle
                    colId={colId}
                    isActive={resizingColumn === colId}
                    label={resizeAriaLabel(colId)}
                    onPointerDownStart={handleResizeStart}
                  />
                </th>
              ))}
            </tr>
            <tr className="border-b border-border-primary bg-bg-secondary/60">
              {columns.map((colId) => (
                <ColumnFilterCell
                  key={colId}
                  colId={colId}
                  value={filters[colId] ?? ''}
                  ariaLabel={t('tenant.documents.vatRegister.editor.filterAriaLabel', {
                    column: labelOf(colId),
                  })}
                  onChangeValue={setColumnFilter}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {value.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-sm text-text-tertiary"
                  colSpan={columns.length}
                >
                  {t('tenant.documents.vatRegister.editor.emptyHint')}
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-sm text-text-tertiary"
                  colSpan={columns.length}
                  data-testid="document-vat-register-filter-no-matches"
                >
                  {t('tenant.documents.vatRegister.editor.filterNoMatches')}
                </td>
              </tr>
            ) : (
              visibleRows.map(({ row, originalIndex }) => (
                <tr
                  key={row.key}
                  className="border-b border-border-primary last:border-0"
                  data-testid={`document-vat-register-row-${originalIndex}`}
                >
                  {columns.map((colId) => renderBodyCell(colId, row, originalIndex))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editable && (
        <Button variant="ghost" onClick={addRow} data-testid="document-vat-register-add-row">
          <Plus className="mr-1 h-4 w-4" />
          {t('tenant.documents.vatRegister.editor.addRow')}
        </Button>
      )}
    </div>
  );
}
